import * as React from 'react';
import moment from 'moment-timezone';
import { useLocation } from 'react-router-dom';
import { Umsatzuebersicht } from './Umsatzuebersicht';
import { UmsatzSummaryGroupBy, UmsatzSummarySource } from './types';
import { UmsatzuebersichtList } from './UmsatzuebersichtList';

export const UmsatzuebersichtPrint: React.FC = () => {
    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);

    const datumVon = urlSearchParams.get('datumVon') || moment().format('YYYY-MM-DD');
    const datumBis = urlSearchParams.get('datumBis') || moment().format('YYYY-MM-DD');

    const kNr = urlSearchParams.get('kNr') ? parseInt(urlSearchParams.get('kNr') || '0', 10) : undefined;
    const kName = urlSearchParams.get('kName') || undefined;

    const sourceS = urlSearchParams.get('source') || '';
    const source: UmsatzSummarySource = ['auftrag', 'rechnung', 'bestellung'].includes(sourceS)
        ? (sourceS as UmsatzSummarySource)
        : 'auftrag';

    const groupBy = urlSearchParams.get('groupBy') as UmsatzSummaryGroupBy;

    const sortColumn = urlSearchParams.get('sortColumn') || 'groupByLabel';
    const sortDirection = urlSearchParams.get('sortDirection') === 'asc' ? 'asc' : 'desc';

    const kundePlzVon = urlSearchParams.get('kundePlzVon')?.trim() || undefined;
    const kundePlzBis = urlSearchParams.get('kundePlzBis')?.trim() || undefined;
    const kundeUmsatzTsdVon = urlSearchParams.get('kundeUmsatzTsdVon')
        ? parseInt(urlSearchParams.get('kundeUmsatzTsdVon') || '0', 10)
        : 0;
    const kundeUmsatzTsdBis = urlSearchParams.get('kundeUmsatzTsdBis')
        ? parseInt(urlSearchParams.get('kundeUmsatzTsdBis') || '0', 10)
        : 0;

    const kundeBranchen = (urlSearchParams.get('kundeBranchen') || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !!n);
    const kundeErweiterteSuchen = (urlSearchParams.get('kundeErweiterteSuchen') || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !!n);
    const kundeInteresseFuerProdukte = (urlSearchParams.get('kundeInteresseFuerProdukte') || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !!n);

    const kundeBetreuer = urlSearchParams.get('kundeBetreuer')
        ? parseInt(urlSearchParams.get('kundeBetreuer') || '0', 10)
        : undefined;
    const kundeTelBetreuer = urlSearchParams.get('kundeTelBetreuer')
        ? parseInt(urlSearchParams.get('kundeTelBetreuer') || '0', 10)
        : undefined;

    const betreuer = urlSearchParams.get('betreuer') ? parseInt(urlSearchParams.get('betreuer') || '0', 10) : undefined;
    const betreuerGebiet = urlSearchParams.get('betreuerGebiet') === 'true';
    const telBetreuer = urlSearchParams.get('telBetreuer')
        ? parseInt(urlSearchParams.get('telBetreuer') || '0', 10)
        : undefined;
    const telBetreuerGebiet = urlSearchParams.get('telBetreuerGebiet') === 'true';

    const produktgruppen = (urlSearchParams.get('produktgruppen') || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !!n);

    const hauptlieferantNr = urlSearchParams.get('hauptlieferantNr')
        ? parseInt(urlSearchParams.get('hauptlieferantNr') || '0', 10)
        : undefined;
    const hauptlieferantName = urlSearchParams.get('hauptlieferantName') || undefined;

    const showDetails = urlSearchParams.get('showDetails') === 'true';
    const addMissingRows1 = urlSearchParams.get('addMissingRows1') === 'true';
    const addMissingRows3 = urlSearchParams.get('addMissingRows3') === 'true';

    const showEkNetto = urlSearchParams.get('showEkNetto') === 'true';
    const showVkNetto = urlSearchParams.get('showVkNetto') === 'true';
    const showBMarge = urlSearchParams.get('showBMarge') === 'true';
    const showNMarge = urlSearchParams.get('showNMarge') === 'true';
    const showBAnteil = urlSearchParams.get('showBAnteil') === 'true';
    const showFAnteil = urlSearchParams.get('showFAnteil') === 'true';

    if (showDetails) {
        return (
            <UmsatzuebersichtList
                source={source}
                groupBy={groupBy}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                datumVon={datumVon}
                datumBis={datumBis}
                kNr={kNr}
                kName={kName}
                kundePlzVon={kundePlzVon}
                kundePlzBis={kundePlzBis}
                kundeUmsatzTsdVon={kundeUmsatzTsdVon}
                kundeUmsatzTsdBis={kundeUmsatzTsdBis}
                kundeBranchen={kundeBranchen}
                kundeErweiterteSuchen={kundeErweiterteSuchen}
                kundeInteresseFuerProdukte={kundeInteresseFuerProdukte}
                kundeBetreuer={kundeBetreuer}
                kundeTelBetreuer={kundeTelBetreuer}
                betreuer={betreuer}
                betreuerGebiet={betreuerGebiet}
                telBetreuer={telBetreuer}
                telBetreuerGebiet={telBetreuerGebiet}
                produktgruppen={produktgruppen}
                hauptlieferantNr={hauptlieferantNr}
                hauptlieferantName={hauptlieferantName}
                showDetails={showDetails}
                addMissingRows1={addMissingRows1}
                addMissingRows3={addMissingRows3}
                printView
            />
        );
    }

    return (
        <Umsatzuebersicht
            source={source}
            groupBy={groupBy}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            toggleSort={() => {}}
            datumVon={datumVon}
            datumBis={datumBis}
            kNr={kNr}
            kName={kName}
            kundePlzVon={kundePlzVon}
            kundePlzBis={kundePlzBis}
            kundeUmsatzTsdVon={kundeUmsatzTsdVon}
            kundeUmsatzTsdBis={kundeUmsatzTsdBis}
            kundeBranchen={kundeBranchen}
            kundeErweiterteSuchen={kundeErweiterteSuchen}
            kundeInteresseFuerProdukte={kundeInteresseFuerProdukte}
            kundeBetreuer={kundeBetreuer}
            kundeTelBetreuer={kundeTelBetreuer}
            betreuer={betreuer}
            betreuerGebiet={betreuerGebiet}
            telBetreuer={telBetreuer}
            telBetreuerGebiet={telBetreuerGebiet}
            produktgruppen={produktgruppen}
            hauptlieferantNr={hauptlieferantNr}
            hauptlieferantName={hauptlieferantName}
            showDetails={showDetails}
            addMissingRows1={addMissingRows1}
            addMissingRows3={addMissingRows3}
            showEkNetto={showEkNetto}
            showVkNetto={showVkNetto}
            showBMarge={showBMarge}
            showNMarge={showNMarge}
            showBAnteil={showBAnteil}
            showFAnteil={showFAnteil}
            printView
        />
    );
};
